import { FC, FocusEventHandler, Ref, SyntheticEvent, useCallback, useImperativeHandle, useRef } from "react";
import { BrokerRecognizedEmployer } from "@goodfill/domain-customer-application-types";
import { Button, Icon, Span, Spinner } from "@goodfill/ui-components";
import { renderAddEmployerManually } from "./renderAddEmployerManually";
import { createNewItemFromQuery } from "./createNewItemFromQuery.util";
import { listEmployeeTracks } from "../../../../backendApi/lambdas/individualApplication/listEmployeeTracks";
import { useRecoilValue } from "recoil";
import { userAuthState } from "../../../../../state";
import { Load } from "@goodfill/load";
import { Select } from "@goodfill/blueprint-select";
import { forwardRef } from "react";

export type RecognizedEmployerSelectProps = {
  selectedRecognizedEmployer: BrokerRecognizedEmployer | null;
  onItemSelect: (item: BrokerRecognizedEmployer, event?: SyntheticEvent) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  disabled?: boolean;
};

export const RecognizedEmployerSelect = forwardRef<HTMLButtonElement, RecognizedEmployerSelectProps>(
  ({ selectedRecognizedEmployer, onItemSelect, onBlur, disabled }, ref) => {
    const { idToken } = useRecoilValue(userAuthState) || {};
    const load = useCallback(() => {
      const fetchEmployeeTracks = async () => {
        const tracks = await listEmployeeTracks(idToken);
        return tracks.employeeTracks;
      };
      return fetchEmployeeTracks();
    }, [idToken]);
    const error = !selectedRecognizedEmployer;
    const itemToStringForSelected = (employer: BrokerRecognizedEmployer) => employer.employerName;
    const placeholder = "Select your employer...";

    return (
      <Load load={load}>
        {(employeeTracks, loading) =>
          loading ? (
            <Spinner />
          ) : (
            <Select
              items={employeeTracks || ([] as BrokerRecognizedEmployer[])}
              onSelect={onItemSelect}
              placeholder={placeholder}
              selected={selectedRecognizedEmployer}
              createNewItemFromQuery={createNewItemFromQuery}
              createNewItemRenderer={renderAddEmployerManually}
              createNewItemPosition={"first"}
              itemToStringForList={(employer: BrokerRecognizedEmployer) => employer.employerName}
              itemToStringId={(employer: BrokerRecognizedEmployer) => employer.employerId}
              itemToStringForSelected={itemToStringForSelected}
              filterInputPlaceholder={"Search, or manually add..."}
              onBlur={onBlur}
              disabled={disabled}
            >
              <Button
                fill
                ref={ref}
                className={`justify-between ${
                  error ? " text-red-500" : ""
                } rounded-[6px] pl-0 shadow-none border border-color-gray`}
              >
                <Span red={error}>
                  {selectedRecognizedEmployer ? itemToStringForSelected(selectedRecognizedEmployer) : placeholder}
                </Span>
                <Icon name="caret-down" />
              </Button>
            </Select>
          )
        }
      </Load>
    );
  },
);
